import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CustomEmptyView from "../../../Components/CustomEmptyView";
import DeleteConfirmationPopup from "../../../Components/DeleteConfirmationPopup";
import Pagination from "../../../Components/Pagination";
import Strings from "../../../Config/Strings";
import { getUserRolePermissions } from "../../../Constants/PermissionConstant";
import {
  deleteUserAPI,
  getLoggedInUserDetailsAPI,
} from "../../../Store/Action/UserAction";
import Colors from "../../../theme/Colors";
import UserTableBody from "../Component/Controls/UserTableBody";
import UserTableHeader from "../Component/Controls/UserTableHeader";
import Icons from "../../../Config/Images";

const UserTable = ({
  users,
  onViewUserDetails,
  rowsPerPage,
  handleChangePage,
  totalItems,
  userRolePermission,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userIdToDelete, setUserIdToDelete] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // Calculate total pages based on totalItems and rowsPerPage
  const totalPages = Math.ceil(totalItems / rowsPerPage);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    handleChangePage(null, newPage - 1); // Adjusting index as per 0-based index system
  };

  // Get permissions based on userRolePermission
  const userPermissions = getUserRolePermissions(userRolePermission);
  const { userData } = useSelector((state) => state.user);

  const handleOpen = useCallback(() => setModalVisible(true), []);
  const handleClose = useCallback(() => setModalVisible(false), []);
  const handleCancelDelete = useCallback(() => setShowDeleteModal(false), []);

  const handleShowDeleteModal = useCallback((userId) => {
    setUserIdToDelete(userId);
    setShowDeleteModal(true);
  }, []);

  const onEditUserDetails = useCallback(
    (user) => {
      navigate("/userList/edit", { state: { user } });
    },
    [navigate]
  );

  useEffect(() => {
    dispatch(getLoggedInUserDetailsAPI({}));
  }, [dispatch]);

  const handleDeleteUser = async () => {
    try {
      await dispatch(
        deleteUserAPI(userIdToDelete, (isSuccess, response) => {
          setShowDeleteModal(false);
          if (isSuccess?.status) {
            toast.success(response?.message);
            setTimeout(() => {
              window.location.reload();
            }, 400);
          } else {
            const errorMessage =
              response?.message || Strings.somethingWentWrongError;
            toast.error(errorMessage);
          }
        })
      );
    } catch (error) {
      console.log("Error dispatching deleteUserAPI:", error);
      toast.error(Strings.somethingWentWrongError);
    }
  };

  return (
    <div style={mainContainer}>
      <div style={outerWrapper}>
        <div style={tableWrapper}>
          <div style={{ height: "92%", minWidth: "110%"}}>
            {users?.length === 0 ||
            users?.length === undefined ||
            users?.length === null ? (
              <div style={{ width: "70vw" }}>
                <CustomEmptyView />
              </div>
            ) : (
              <>
                <table style={table}>
                  <UserTableHeader />
                  <UserTableBody
                    users={users}
                    onViewUserDetails={onViewUserDetails}
                    onEditUserDetails={onEditUserDetails}
                    handleShowDeleteModal={handleShowDeleteModal}
                    onViewBtnShow={
                      userData?.data?.user_role_name === Strings.Admin
                        ? true
                        : userPermissions.viewUserBtn
                    }
                    onEditBtnShow={
                      userData?.data?.user_role_name === Strings.Admin
                        ? true
                        : userPermissions.editUserBtn
                    }
                    onDeleteBtnShow={
                      userData?.data?.user_role_name === Strings.Admin
                        ? true
                        : userPermissions.deleteUserBtn
                    }
                  />
                </table>
              </>
            )}
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      </div>

      {showDeleteModal && (
        <DeleteConfirmationPopup
          isVisible={showDeleteModal}
          onCancel={handleCancelDelete}
          onDelete={handleDeleteUser}
          title={Strings.deleteTitle}
          icons={Icons.deleteWithCircle}
        />
      )}
    </div>
  );
};

const mainContainer = {
  borderRadius: "10px",
  height: "100%",
  margin: "0px 8px",
};

const outerWrapper = {
  borderRadius: "4px",
  boxShadow: "0px 0px 3px #e6e6e6",
  maxWidth: "100%",
  height: "100%",
  backgroundColor: Colors.white,
};

const tableWrapper = {
  overflowY: "scroll",
  overflowX: "scroll",
  height: "100%",
};

const table = {
  minWidth: "110%",
  borderCollapse: "separate",
  borderSpacing: "0px",
};

export default UserTable;
